import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  deepMergeFilters,
  flattenScopedFiltersToQuery,
  hasSignificantFilterValues,
  parseScopedFiltersQuery,
  removeScopedFilterKeys
} from './filtersQuery'

/**
 * Sync scoped URL query filters under `filters[scopeKey][…]`.
 *
 * - `initialValue`: merged under URL-derived values (query wins on conflicts). Omitted = `{}`.
 * - `updateFilters`: deep-merges nested plain objects; **arrays are replaced** entirely.
 * - `clearFilters`: removes only keys for this `scopeKey`; other query params stay.
 */
export function useFilters(
  scopeKey: string,
  initialValue: Record<string, unknown> = {}
) {
  const route = useRoute()
  const router = useRouter()

  const currentFilters = computed(() =>
    deepMergeFilters(
      initialValue,
      parseScopedFiltersQuery(route.query, scopeKey)
    )
  )

  const enabled = computed(() =>
    hasSignificantFilterValues(currentFilters.value)
  )

  function updateFilters(partial: Record<string, unknown>): void {
    const merged = deepMergeFilters(currentFilters.value, partial)
    const base = removeScopedFilterKeys(route.query, scopeKey)
    const flat = flattenScopedFiltersToQuery(scopeKey, merged)
    router.replace({
      query: { ...base, ...flat }
    })
  }

  function clearFilters(): void {
    router.replace({
      query: removeScopedFilterKeys(route.query, scopeKey)
    })
  }

  return {
    enabled,
    currentFilters,
    updateFilters,
    clearFilters
  }
}
